import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ImagePlus, ShoppingBag, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { whatsappOrderLink } from "@/lib/whatsapp";
import { isMumkey, sectionLabel, slugify } from "@/lib/categories";
import { baseName, colorOf, variantColor } from "@/lib/variants";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function Stars({ value, size = "h-4 w-4" }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= value ? "fill-obsidian text-obsidian" : "text-seam"}`} />
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewsData, setReviewsData] = useState({ reviews: [], average: 0, count: 0 });
  const [form, setForm] = useState({ name: "", rating: 0, text: "", image: "" });
  const [submitting, setSubmitting] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setActiveImg(0);
    setVariants([]);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        api
          .get("/products", { params: { category: r.data.category } })
          .then((res) => {
            const sibs = res.data.filter((v) => baseName(v.name) === baseName(r.data.name));
            sibs.sort((a, b) => (variantColor(a.name) ? 1 : 0) - (variantColor(b.name) ? 1 : 0) || colorOf(a).localeCompare(colorOf(b)));
            setVariants(sibs);
          })
          .catch(() => {});
      })
      .catch(() => setProduct(undefined));
    api
      .get(`/products/${id}/reviews`)
      .then((r) => setReviewsData(r.data))
      .catch(() => {});
  }, [id]);

  if (product === null) {
    return <div className="px-6 py-32 text-soot" data-testid="product-loading">Unfolding the piece…</div>;
  }
  if (product === undefined) {
    return (
      <div className="px-6 py-32" data-testid="product-not-found">
        <p className="font-serif text-3xl italic text-soot">This piece is no longer available.</p>
        <Link to="/shop" className="mt-4 inline-block text-xs font-bold uppercase tracking-[0.18em] underline underline-offset-8">
          Back to Shop
        </Link>
      </div>
    );
  }

  const mumkey = isMumkey(product);
  const needsSize = product.sizes?.length > 0;
  const soldOut = product.stock === 0;

  const handleOrder = (e) => {
    if (needsSize && !size) {
      e.preventDefault();
      toast.error("Please select a size first");
    }
  };

  const handleAddToCart = () => {
    if (needsSize && !size) {
      toast.error("Please select a size first");
      return;
    }
    addItem(product, size, 1);
    toast.success(`${product.name} added to your bag`);
  };

  const loadReviews = () => {
    api
      .get(`/products/${id}/reviews`)
      .then((r) => setReviewsData(r.data))
      .catch(() => {});
  };

  const uploadReviewPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Photo must be 3 MB or smaller");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setPhotoUploading(true);
    try {
      const { data } = await api.post("/uploads/review", fd);
      setForm((f) => ({ ...f, image: data.url }));
    } catch {
      toast.error("Photo upload failed. Try a different image.");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Please choose a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, { name: form.name, rating: form.rating, text: form.text, image: form.image });
      toast.success("Thank you — your review is live");
      setForm({ name: "", rating: 0, text: "", image: "" });
      loadReviews();
    } catch {
      toast.error("Could not post your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="product-detail-page" className={`px-6 py-12 md:px-12 lg:px-20 lg:py-20 ${mumkey ? "bg-[#FAF1ED]" : ""}`}>
      <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-2 lg:gap-20">
        <div className="self-start lg:sticky lg:top-28">
          <motion.div
            key={activeImg}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className={`overflow-hidden ${mumkey ? "rounded-t-[180px] border-8 border-[#F3DEDA]" : "bg-linen"}`}
          >
            {product.images?.[activeImg] && (
              <img
                src={product.images[activeImg]}
                alt={product.name}
                data-testid="product-image"
                className="aspect-[3/4] max-h-[80vh] w-full object-cover"
              />
            )}
          </motion.div>
          {product.images?.length > 1 && (
            <div className="mt-4 flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-16 overflow-hidden border transition-colors ${
                    i === activeImg ? "border-obsidian" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          {mumkey ? (
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#B76E79]" data-testid="product-breadcrumb">
              Mumkey{product.subcategory ? ` · ${product.subcategory}` : ""}
            </p>
          ) : (
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot" data-testid="product-breadcrumb">
              {sectionLabel(product.category)}{product.subcategory ? ` / ${product.subcategory}` : ""}
            </p>
          )}
          <h1
            className={`mt-4 font-serif text-4xl font-light tracking-tight sm:text-5xl md:text-6xl ${mumkey ? "text-[#4A2C2A]" : ""}`}
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <p className="mt-4 text-2xl font-semibold" data-testid="product-price">{inr(product.price)}</p>

          <p className={`mt-8 max-w-lg text-base leading-relaxed ${mumkey ? "text-[#7A5A55]" : "text-soot"}`}>{product.description}</p>

          {needsSize && (
            <div className="mt-10">
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${mumkey ? "text-[#B76E79]" : "text-soot"}`}>Size</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-option-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-12 border px-4 py-2.5 text-sm transition-colors duration-200 ${
                      size === s
                        ? mumkey
                          ? "border-[#B76E79] bg-[#B76E79] text-white"
                          : "border-obsidian bg-obsidian text-bone"
                        : mumkey
                          ? "border-[#EDD3CE] text-[#4A2C2A] hover:border-[#B76E79]"
                          : "border-seam hover:border-obsidian"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variants.length > 1 && (
            <div className="mt-10">
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${mumkey ? "text-[#B76E79]" : "text-soot"}`}>Colour</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="color-picker">
                {variants.map((v) => (
                  <Link
                    key={v.id}
                    to={`/product/${v.id}`}
                    data-testid={`color-option-${slugify(colorOf(v))}`}
                    className={`border px-4 py-2.5 text-sm transition-colors duration-200 ${
                      v.id === product.id
                        ? mumkey
                          ? "border-[#B76E79] bg-[#B76E79] text-white"
                          : "border-obsidian bg-obsidian text-bone"
                        : mumkey
                          ? "border-[#EDD3CE] text-[#4A2C2A] hover:border-[#B76E79]"
                          : "border-seam hover:border-obsidian"
                    }`}
                  >
                    {colorOf(v)}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10">
            {soldOut ? (
              <p className="inline-block bg-obsidian px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-bone" data-testid="sold-out-label">
                Sold Out
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-6">
                <Button
                  data-testid="add-to-cart-button"
                  onClick={handleAddToCart}
                  className="h-12 rounded-full bg-obsidian px-10 text-xs font-bold uppercase tracking-[0.18em] text-bone transition-all duration-300 hover:scale-[1.03] hover:bg-olive"
                >
                  <ShoppingBag className="h-4 w-4" /> Add to Bag
                </Button>
                <Button
                  asChild
                  data-testid="whatsapp-order-button"
                  className="h-12 rounded-full bg-[#25D366] px-10 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_-8px_rgba(37,211,102,0.6)] transition-all duration-300 hover:scale-[1.03] hover:bg-[#1fb857]"
                >
                  <a
                    href={whatsappOrderLink(product, size)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleOrder}
                  >
                    <WhatsAppIcon className="h-4 w-4" /> Order on WhatsApp
                  </a>
                </Button>
              </div>
            )}
            <p className={`mt-4 max-w-md text-sm ${mumkey ? "text-[#7A5A55]" : "text-soot"}`}>
              Tap to open a WhatsApp chat with us — we&rsquo;ll confirm availability, payment and delivery right there.
            </p>
          </div>

          <div className={`mt-12 space-y-3 border-t pt-8 text-sm ${mumkey ? "border-[#EDD3CE] text-[#7A5A55]" : "border-seam text-soot"}`}>
            <p>— {product.stock > 0 ? `${product.stock} pieces in stock` : "Currently sold out"}</p>
            <p>— Delivery across India, arranged over WhatsApp</p>
            <p>— Easy 7-day returns, no questions asked</p>
          </div>
        </motion.div>
      </div>

      {/* REVIEWS */}
      <section className="mx-auto mt-20 max-w-[1400px] border-t border-seam pt-14" data-testid="reviews-section">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">Customer Reviews</p>
            <div className="mt-4 flex items-end gap-4">
              <p className="font-serif text-6xl font-light leading-none" data-testid="reviews-average">
                {reviewsData.count ? reviewsData.average : "—"}
              </p>
              <div className="pb-1">
                <Stars value={Math.round(reviewsData.average)} />
                <p className="mt-1 text-sm text-soot" data-testid="reviews-count">
                  {reviewsData.count} review{reviewsData.count === 1 ? "" : "s"}
                </p>
              </div>
            </div>

            <form onSubmit={submitReview} className="mt-10 space-y-4" data-testid="review-form">
              <p className="font-serif text-2xl">Write a review</p>
              <Input
                data-testid="review-name-input"
                required
                maxLength={80}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="bg-transparent"
              />
              <div className="flex items-center gap-1" data-testid="review-rating-input">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className="p-0.5 transition-transform duration-150 hover:scale-110"
                  >
                    <Star className={`h-6 w-6 ${n <= form.rating ? "fill-obsidian text-obsidian" : "text-seam"}`} />
                  </button>
                ))}
              </div>
              <Textarea
                data-testid="review-text-input"
                required
                minLength={3}
                maxLength={1000}
                rows={3}
                placeholder="What did you love about it?"
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                className="bg-transparent"
              />
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  id="review-photo-input"
                  className="hidden"
                  onChange={uploadReviewPhoto}
                  data-testid="review-photo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="review-photo-button"
                  disabled={photoUploading}
                  onClick={() => document.getElementById("review-photo-input")?.click()}
                >
                  <ImagePlus className="h-4 w-4" /> {photoUploading ? "Uploading…" : "Add Photo"}
                </Button>
                {form.image && (
                  <div className="relative h-14 w-11 overflow-hidden border border-seam">
                    <img src={form.image} alt="" className="h-full w-full object-cover" data-testid="review-photo-preview" />
                    <button
                      type="button"
                      data-testid="review-photo-remove"
                      aria-label="Remove photo"
                      onClick={() => setForm((f) => ({ ...f, image: "" }))}
                      className="absolute right-0 top-0 bg-obsidian/80 p-0.5 text-bone"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
              <Button
                type="submit"
                data-testid="review-submit-button"
                disabled={submitting}
                className="h-11 rounded-full bg-obsidian px-8 text-xs font-bold uppercase tracking-[0.18em] text-bone hover:bg-olive"
              >
                {submitting ? "Posting…" : "Post Review"}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-8" data-testid="reviews-list">
            {reviewsData.reviews.length === 0 ? (
              <p className="font-serif text-2xl italic text-soot" data-testid="reviews-empty">
                No reviews yet — be the first to share your experience.
              </p>
            ) : (
              <div className="space-y-8">
                {reviewsData.reviews.map((r) => (
                  <div key={r.id} className="border-b border-seam pb-6" data-testid={`review-item-${r.id}`}>
                    <Stars value={r.rating} />
                    {r.image && (
                      <img
                        src={r.image}
                        alt={`${r.name}'s purchase`}
                        data-testid={`review-photo-${r.id}`}
                        className="mt-3 h-28 w-24 border border-seam object-cover"
                      />
                    )}
                    <p className="mt-3 max-w-2xl text-base leading-relaxed">{r.text}</p>
                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.15em] text-soot">
                      {r.name} · {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
